import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Building2,
  Clock3,
  ClipboardList,
  Dices,
  Hourglass,
  Loader2,
  RadioTower,
  RefreshCw,
  Users,
  Video,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { where, Timestamp as FsTimestamp } from 'firebase/firestore';
import MainLayout from '../../components/layout/MainLayout';
import { functions } from '../../config/firebase';
import { esProduccion } from '../../config/entorno';
import { useDocument, useCollection } from '../../hooks/useFirestore';
import { PisoTopDown } from '../../components/piso/PisoTopDown';
import { MapaCalor, type FilaMapaCalor } from '../../components/admin/MapaCalor';
import type { Sala, Consulta } from '../../types';
import styles from './MonitorEnVivoPage.module.css';

/** Fecha ISO en hora de Panamá, que es la que define el "día" de la operación. */
function isoPanama(desplazamientoDias = 0): string {
  const ahora = new Date(Date.now() + desplazamientoDias * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(ahora);
}

/** Hora del día (0-23) en Panamá de un instante dado. */
function horaPanama(ms: number): number {
  return Number(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Panama', hour: '2-digit', hour12: false,
  }).format(new Date(ms)));
}

const HORAS_LBL = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

interface TriageDailyHoy {
  /** Hora ('0'..'23') → salida del triage → conteo. */
  porHora?: Record<string, Record<string, number>>;
}

// ── Shape del doc agregado que escribe el cron `reconciliarCuposEnVivo` ──
interface PorSalaEntry {
  enEspera: number;
  enAtencion: number;
  enTriage: number;
  cupoDisponible: number;
  // CAP (capacidad dinámica): campos aditivos del cron.
  capacidadDinamica?: number;
  cupoDinamicoDisponible?: number;
  medicosEnTurno?: number;
  medicosLibresAhora?: number;
  motivoSinCupo?: string;
}
interface FunnelEnVivo {
  id?: string;
  conectados?: number;
  enTriage?: number;
  enEspera?: number;
  enAtencion?: number;
  totalOcupado?: number;
  salasActivas?: number;
  porSala?: Record<string, PorSalaEntry>;
  actualizadoEn?: { toMillis?: () => number } | null;
}

function fmtHace(seg: number | null): string {
  if (seg === null) return 'esperando primer dato…';
  if (seg < 90) return `hace ${seg}s`;
  return `hace ${Math.round(seg / 60)} min`;
}

export default function MonitorEnVivoPage() {
  const { data: funnel, loading } = useDocument<FunnelEnVivo>('metricas', 'funnelEnVivo');
  const { data: salas } = useCollection<Sala>('salas');

  // ── Actividad de HOY por hora (issue: combinar KPIs + embudo en un mapa de
  //    calor, mismo tipo que /tv/operacion). "Conectados" es presencia en
  //    vivo (Realtime Database) sin historial por hora en ningún lado del
  //    sistema — queda fuera del mapa y se muestra como cifra viva en el
  //    encabezado de la sección. "Cupo disponible" tampoco es un evento con
  //    hora (es capacidad AHORA, según quién está conectado en este
  //    instante) — ya se ve en el banner de arriba y en la tabla por sala.
  //    "Solicitando" (otro estado de presencia en vivo) se retiró de todo el
  //    monitor: la ventana en la que un paciente está en ese estado dura lo
  //    que tarda UNA llamada a crearConsulta (unos cientos de ms), y el cron
  //    que llena este documento corre una vez por minuto — muestrear un
  //    pulso de <2 s con un sondeo de 60 s prácticamente nunca lo captura.
  //    El contador quedaba en 0 casi siempre sin importar la demanda real:
  //    no es una limitación de la vista, el dato de origen no es medible así. ──
  const restriccionesHoy = useMemo(() => [
    where('creadoEn', '>=', FsTimestamp.fromDate(new Date(`${isoPanama(0)}T00:00:00-05:00`))),
    where('creadoEn', '<', FsTimestamp.fromDate(new Date(`${isoPanama(1)}T00:00:00-05:00`))),
  ], []);
  const { data: consultasHoy } = useCollection<Consulta>('consultas', restriccionesHoy);

  const restriccionesTriageHoy = useMemo(() => [where('fecha', '==', isoPanama(0))], []);
  const { data: triageDocsHoy } = useCollection<TriageDailyHoy>('triage_stats_daily', restriccionesTriageHoy);

  const filasPorHora = useMemo<FilaMapaCalor[]>(() => {
    const enTriageHora = HORAS_LBL.map(() => 0);
    const enEsperaHora = HORAS_LBL.map(() => 0);
    const enAtencionHora = HORAS_LBL.map(() => 0);

    for (const t of triageDocsHoy ?? []) {
      for (const [hh, porResultado] of Object.entries(t.porHora ?? {})) {
        const h = Number(hh);
        if (!(h >= 0 && h <= 23)) continue;
        enTriageHora[h] += Object.values(porResultado).reduce((a, b) => a + (b || 0), 0);
      }
    }
    for (const c of consultasHoy ?? []) {
      const msCreado = c.creadoEn?.toMillis?.();
      if (msCreado) enEsperaHora[horaPanama(msCreado)] += 1;
      const msIniciado = c.iniciadaEn?.toMillis?.();
      if (msIniciado) enAtencionHora[horaPanama(msIniciado)] += 1;
    }

    return [
      { clave: 'triage', etiqueta: 'En triage', color: '#8b5cf6', valores: enTriageHora },
      { clave: 'espera', etiqueta: 'En espera', color: '#f59e0b', valores: enEsperaHora },
      { clave: 'atencion', etiqueta: 'En atención', color: '#3b82f6', valores: enAtencionHora },
    ];
  }, [consultasHoy, triageDocsHoy]);
  const hayDatosPorHora = filasPorHora.some((f) => f.valores.some((v) => v > 0));

  // Tick de 1s para refrescar el "actualizado hace Xs".
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Refresco manual: re-dispara el cálculo del funnel (mismo core que el cron).
  // El doc metricas/funnelEnVivo se actualiza solo por la suscripción en vivo.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const refrescar = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      await httpsCallable(functions, 'portal_recalcularFunnelEnVivo')();
    } catch {
      setRefreshError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Simulación local (datos de prueba): no toca Firestore, solo sobreescribe la
  // vista. Conectados 100+; el resto ≤100. Re-simular genera nuevos números.
  const [sim, setSim] = useState<Record<string, number> | null>(null);
  const simular = useCallback(() => {
    const r = (max: number) => Math.floor(Math.random() * max);
    setSim({
      conectado: 100 + r(400),
      triage: r(101),
      espera: r(101),
      atencion: r(101),
    });
  }, []);
  const salirSim = useCallback(() => setSim(null), []);

  const salaNombre = useMemo(() => {
    const m = new Map<string, string>();
    (salas ?? []).forEach((s) => m.set(s.id, s.nombre));
    return m;
  }, [salas]);

  const conectados = sim ? sim.conectado : (funnel?.conectados ?? 0);
  const enTriage = sim ? sim.triage : (funnel?.enTriage ?? 0);
  const enEspera = sim ? sim.espera : (funnel?.enEspera ?? 0);
  const enAtencion = sim ? sim.atencion : (funnel?.enAtencion ?? 0);
  const totalSistema = funnel?.totalOcupado ?? enTriage + enEspera + enAtencion;

  const actualizadoMs = funnel?.actualizadoEn?.toMillis?.() ?? 0;
  const haceSeg = actualizadoMs ? Math.max(0, Math.round((now - actualizadoMs) / 1000)) : null;
  const stale = haceSeg !== null && haceSeg > 420; // el cron reescribe al cambiar algo o cada 5 min

  const porSalaArr = useMemo(() => {
    const ps = funnel?.porSala ?? {};
    return Object.entries(ps)
      .map(([salaId, v]) => ({
        salaId,
        nombre: salaNombre.get(salaId) ?? salaId,
        enTriage: v.enTriage ?? 0,
        enEspera: v.enEspera ?? 0,
        enAtencion: v.enAtencion ?? 0,
        cupoDisponible: v.cupoDisponible ?? 0,
        medicosEnTurno: v.medicosEnTurno ?? 0,
        medicosLibresAhora: v.medicosLibresAhora ?? 0,
        capacidadDinamica: v.capacidadDinamica ?? 0,
        cupoDinamicoDisponible: v.cupoDinamicoDisponible ?? 0,
        motivoSinCupo: v.motivoSinCupo ?? 'ok',
      }))
      .sort(
        (a, b) =>
          b.enTriage + b.enEspera + b.enAtencion - (a.enTriage + a.enEspera + a.enAtencion),
      );
  }, [funnel, salaNombre]);

  const totalCupoDisponible = porSalaArr.reduce((s, x) => s + x.cupoDisponible, 0);

  const stages = [
    { key: 'conectado', label: 'Conectados', value: conectados, color: '#0ea5e9', icon: RadioTower, hint: 'navegando teleconsulta' },
    { key: 'triage', label: 'En triage', value: enTriage, color: '#8b5cf6', icon: ClipboardList, hint: 'reservando cupo (holds)' },
    { key: 'espera', label: 'En espera', value: enEspera, color: '#f59e0b', icon: Hourglass, hint: 'en cola de atención' },
    { key: 'atencion', label: 'En atención', value: enAtencion, color: '#3b82f6', icon: Video, hint: 'con médico' },
  ];

  if (loading) {
    return (
      <MainLayout pageTitle="Monitor en vivo">
        <div className={styles.loadingWrap}>
          <Loader2 size={32} className={styles.spin} />
          <span>Cargando monitor…</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout pageTitle="Monitor en vivo">
      <div className={styles.page}>
        {/* ─── Header ─────────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <RadioTower size={24} />
            <div>
              <h1 className={styles.title}>Monitor de demanda en vivo</h1>
              <p className={styles.subtitle}>Pacientes intentando atenderse, por etapa del embudo</p>
            </div>
          </div>
          <div className={styles.headerRight}>
            {refreshError && <span className={styles.refreshErr}>No se pudo refrescar</span>}
            <div className={`${styles.liveRow} ${sim ? styles.liveSim : stale ? styles.liveStale : ''}`}>
              <span className={styles.liveDot} />
              <span>
                {sim ? 'datos simulados (prueba)' : `${stale ? 'datos desactualizados' : 'en vivo'} · ${fmtHace(haceSeg)}`}
              </span>
            </div>
            {sim && (
              <button type="button" className={styles.refreshBtn} onClick={salirSim} title="Volver a datos en vivo">
                <span>Salir</span>
              </button>
            )}
            {/* Simular pinta números inventados sobre el monitor. Es una ayuda
                para probar la vista sin pacientes reales, y en producción no
                tiene sentido: quien la use ahí estaría mirando datos falsos de
                un servicio en operación. Se oculta fuera de development. */}
            {!esProduccion && (
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={simular}
                title="Generar pacientes de prueba en las colas (no afecta los datos reales)"
              >
                <Dices size={14} />
                <span>{sim ? 'Re-simular' : 'Simular'}</span>
              </button>
            )}
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={refrescar}
              disabled={refreshing || !!sim}
              title="Recalcular los contadores ahora"
            >
              <RefreshCw size={14} className={refreshing ? styles.spin : undefined} />
              <span>{refreshing ? 'Actualizando…' : 'Refrescar'}</span>
            </button>
          </div>
        </div>

        {/* ─── Banner total ───────────────────────────── */}
        <div className={styles.banner}>
          <Activity size={16} />
          <span>
            <strong>{totalSistema}</strong> pacientes en el sistema ahora ·{' '}
            <strong>{totalCupoDisponible}</strong> cupos disponibles ·{' '}
            <strong>{funnel?.salasActivas ?? porSalaArr.length}</strong> salas activas
          </span>
        </div>

        {/* ─── Actividad de hoy · por hora (antes: KPI cards + embudo) ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Clock3 size={16} /> Actividad de hoy · por hora
            </h2>
            <span className={styles.sectionBadge}>{conectados} conectados ahora</span>
          </div>
          <div className={styles.sectionBody}>
            {hayDatosPorHora ? (
              <MapaCalor columnas={HORAS_LBL} filas={filasPorHora} />
            ) : (
              <p className={styles.emptyState}>Sin actividad registrada hoy todavía.</p>
            )}
          </div>
        </div>

        {/* ─── Vista de piso (top-down animada) ───────── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Users size={16} /> Vista de piso en vivo
            </h2>
            <span className={styles.sectionBadge}>top-down</span>
          </div>
          <div className={styles.floorBody}>
            <PisoTopDown
              etapas={stages.map((st) => {
                const Icon = st.icon;
                return {
                  clave: st.key,
                  etiqueta: st.label,
                  valor: st.value,
                  color: st.color,
                  icono: <Icon size={16} />,
                  cuelloBotella: st.key === 'espera' && enEspera > 0 && totalCupoDisponible === 0,
                };
              })}
            />
          </div>
        </div>

        {/* ─── Por sala ───────────────────────────────── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Building2 size={16} /> Detalle por sala
            </h2>
            <span className={styles.sectionBadge}>{porSalaArr.length} salas</span>
          </div>
          {porSalaArr.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Sala</th>
                    <th className={styles.numCell}>Médicos (turno/libres)</th>
                    <th className={styles.numCell}>En triage</th>
                    <th className={styles.numCell}>En espera</th>
                    <th className={styles.numCell}>En atención</th>
                    <th className={styles.numCell}>Cupo disponible</th>
                    <th className={styles.numCell}>Cupo dinámico</th>
                  </tr>
                </thead>
                <tbody>
                  {porSalaArr.map((s) => (
                    <tr key={s.salaId}>
                      <td className={styles.salaName}>
                        {s.nombre}
                        {s.motivoSinCupo === 'sin_medicos' && (
                          <span className={styles.cupoCero}> · sin médicos</span>
                        )}
                        {s.motivoSinCupo === 'tope' && (
                          <span className={styles.cupoCero}> · saturada</span>
                        )}
                      </td>
                      <td className={styles.numCell}>{s.medicosEnTurno} / {s.medicosLibresAhora}</td>
                      <td className={styles.numCell}>{s.enTriage}</td>
                      <td className={styles.numCell}>{s.enEspera}</td>
                      <td className={styles.numCell}>{s.enAtencion}</td>
                      <td className={styles.numCell}>
                        <span className={s.cupoDisponible === 0 ? styles.cupoCero : styles.cupoOk}>
                          {s.cupoDisponible}
                        </span>
                      </td>
                      <td className={styles.numCell}>
                        <span className={s.cupoDinamicoDisponible === 0 ? styles.cupoCero : styles.cupoOk}>
                          {s.cupoDinamicoDisponible}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.sectionBody}>
              <p className={styles.emptyState}>
                Sin datos de salas todavía. El monitor se llena cuando el cron{' '}
                <code>reconciliarCuposEnVivo</code> corre (cada minuto) y hay salas activas.
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
